/**
 * Rules of the shell's promotional layers (P11), with the current site's storage keys so a visitor who
 * dismissed something on the old site does not see it again (docs/legacy-audit/STORAGE.md L7, L9, S1):
 *
 * - app banner (L9 `appBannerDismissed`, epoch ms): iPhone/iPad/iPod/Android only, hidden for 24 h after
 *   «Fermer» (Decision 22: the legacy constant is named one day but held one hour);
 * - online-payment announcement (L7 `cardnd_payment_popup_count`): home page, after 1.5 s, at most 3 times;
 * - marketing consent (S1 `marketing_modal_shown`, per tab): signed in and `marketingConsent !== true`,
 *   after 4 s; both answers set the flag.
 */
import { readRaw, readRawSession, writeRaw, writeRawSession } from '@/services/storage';

export const APP_BANNER_KEY = 'appBannerDismissed';
export const APP_BANNER_TTL_MS = 24 * 60 * 60 * 1000;
export const PAYMENT_POPUP_KEY = 'cardnd_payment_popup_count';
export const PAYMENT_POPUP_MAX = 3;
export const PAYMENT_POPUP_DELAY_MS = 1500;
export const MARKETING_KEY = 'marketing_modal_shown';
export const MARKETING_DELAY_MS = 4000;

export const APP_STORE_URL = 'https://apps.apple.com/ma/app/cardnd/id6754260216';
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.openlluna.cardnd';

export type Store = 'ios' | 'android';

/** The phone's store, from the user agent (legacy detection), or null on a computer. */
export function storeFor(userAgent: string): Store | null {
  const ua = userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  if (/android/.test(ua)) return 'android';
  return null;
}

/** Banner shown on a phone when never dismissed, or dismissed more than 24 h ago. */
export function appBannerVisible(userAgent: string, dismissedAt: string | null, now: number): boolean {
  if (!storeFor(userAgent)) return false;
  if (!dismissedAt) return true;
  const at = Number.parseInt(dismissedAt, 10);
  return !Number.isFinite(at) || now - at > APP_BANNER_TTL_MS;
}

/** The legacy counter (`parseInt(value || '0', 10)`); anything unreadable counts as 0. */
export function popupCount(raw: string | null): number {
  const n = Number.parseInt(raw || '0', 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export const paymentPopupDue = (raw: string | null) => popupCount(raw) < PAYMENT_POPUP_MAX;

/** Ask for marketing consent: signed in, not accepted yet, not asked in this tab. */
export function consentDue(user: { marketingConsent?: boolean | null } | null | undefined, shown: string | null) {
  return !!user && user.marketingConsent !== true && !shown;
}

/**
 * No promotional layer for automated browsers (crawlers, monitoring, visual tests): `navigator.webdriver`.
 * Search engines also penalise interstitials.
 */
export const automated = () => typeof navigator !== 'undefined' && navigator.webdriver === true;

/* ── Storage (guarded helpers of services/storage) ── */
export const promoStorage = {
  bannerDismissedAt: () => readRaw(APP_BANNER_KEY),
  dismissBanner: (now = Date.now()) => writeRaw(APP_BANNER_KEY, String(now)),
  paymentPopupCount: () => readRaw(PAYMENT_POPUP_KEY),
  /** Counted when shown (legacy: incremented inside the timer, before the modal opens). */
  countPaymentPopup: () => writeRaw(PAYMENT_POPUP_KEY, String(popupCount(readRaw(PAYMENT_POPUP_KEY)) + 1)),
  consentShown: () => readRawSession(MARKETING_KEY),
  markConsentShown: () => writeRawSession(MARKETING_KEY, 'true'),
};
